import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { TrendBadge } from "./TrendBadge";
import { ReactNode } from "react";

interface KPICardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  trend?: number;
  previousValue?: string | number;
  loading?: boolean;
  subtitle?: string;
}

export function KPICard({
  title,
  value,
  icon,
  trend,
  previousValue,
  loading = false,
  subtitle,
}: KPICardProps) {
  const getTrendIcon = () => {
    if (!trend) return Minus;
    return trend > 0 ? TrendingUp : TrendingDown;
  };

  const TrendIcon = getTrendIcon();
  const isPositive = trend !== undefined && trend > 0;
  const isNegative = trend !== undefined && trend < 0;

  return (
    <Card className="relative overflow-hidden transition-all hover:shadow-hover animate-fade-in">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
      
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
        <div className="flex flex-col">
          <p className="text-sm font-medium text-muted-foreground">
            {title}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground/70 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <div className="rounded-lg bg-primary/10 p-2.5">
          {icon}
        </div>
      </CardHeader>
      
      <CardContent className="relative">
        {loading ? (
          <div className="space-y-2">
            <div className="h-10 w-32 bg-muted animate-pulse rounded" />
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold tracking-tight">
                {value}
              </div>
              {trend !== undefined && trend !== 0 && (
                <TrendBadge 
                  value={trend} 
                  isPositive={isPositive}
                  showIcon 
                />
              )}
            </div>
            
            {previousValue !== undefined && (
              <div className="flex items-center gap-2 mt-2">
                <TrendIcon className={cn(
                  "h-3.5 w-3.5",
                  isPositive && "text-emerald-500",
                  isNegative && "text-rose-500",
                  !isPositive && !isNegative && "text-muted-foreground"
                )} />
                <p className="text-xs text-muted-foreground">
                  vs période précédente: <span className="font-medium">{previousValue}</span>
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
